// src/pages/CostCodes.tsx
// Client Cost Code Library — admin page
// Phase 1 of CostCode_FunctionSpec.docx
//
// Per-client MRS-based cost code management for TMR, Council, Energex.
// Structure-first: TMR library holds illustrative seed items until the
// official TMR Estimate Import Template is loaded via bulk import.
// A13 CA Agent and A12 QS Agent monitor library status.

import { useState, useMemo } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  getAllLibraries, getLibrary, getActiveItems, deactivateItem, addCostCodeItem,
  simulateBulkImport, addClientLibrary,
  CLIENT_TYPE_META, COST_CODE_TYPE_META, EXPENSE_CLASS_META, IMPORT_STATUS_META,
  TMR_MRS_GROUPS,
  CostCodeItem, ClientCostLibrary, CostCodeType, CostCodeClientType, ExpenseClass,
} from '../utils/mockDataCostCodes'
import {
  Tag, Upload, Plus, AlertTriangle, CheckCircle, Info,
  Download, ExternalLink, ChevronDown, ChevronUp, X,
  RefreshCw, Clock, Search, Filter, FileSpreadsheet, Bot,
  AlertCircle, Eye, EyeOff, History,
} from 'lucide-react'

const fmtAUD = (n: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 }).format(n)
const fmtDT = (iso: string) =>
  new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })

// ── Excel column spec (shown in the import panel) ─────────────────

const EXCEL_COLUMNS = [
  { col: 'A', header: 'mrs_group',    note: 'e.g. MRS93',              required: true  },
  { col: 'B', header: 'work_item_id', note: 'e.g. 93001L',             required: true  },
  { col: 'C', header: 'description',  note: 'Full client description',  required: true  },
  { col: 'D', header: 'unit',         note: 'ea / m / m² / hr / LS',   required: true  },
  { col: 'E', header: 'rate_ex_gst',  note: 'Numeric, ex GST',         required: true  },
  { col: 'F', header: 'type',         note: 'L / M / E / C',           required: false },
  { col: 'G', header: 'expense_class',note: 'CAPEX or OPEX',            required: false },
  { col: 'H', header: 'wi_reference', note: 'Client WI doc ref',       required: false },
  { col: 'I', header: 'effective_date',note: 'DD/MM/YYYY',             required: false },
  { col: 'J', header: 'notes',        note: 'Internal notes',          required: false },
]

// ── Import simulation panel ───────────────────────────────────────

function ImportPanel({ lib, onImported }: { lib: ClientCostLibrary; onImported: () => void }) {
  const { user } = useAuthStore()
  const [step, setStep] = useState<'idle' | 'validating' | 'preview' | 'done'>('idle')
  const [scheduleVer, setScheduleVer] = useState('')

  const simulateUpload = () => {
    setStep('validating')
    setTimeout(() => setStep('preview'), 1200)
  }

  const confirmImport = () => {
    // Simulate replacing illustrative items with "official" data
    // In production this would parse the actual uploaded Excel file
    simulateBulkImport(
      lib.clientId,
      lib.items.map(i => ({ ...i, isIllustrative: false })),
      user?.fullName ?? 'PM',
      user?.id ?? 'u-sys',
      scheduleVer || `${CLIENT_TYPE_META[lib.clientType].abbr} Estimate Import Template — ${new Date().getFullYear()}`,
    )
    setStep('done')
    setTimeout(onImported, 800)
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 bg-[#1F3864] flex items-center gap-3">
        <FileSpreadsheet className="w-4 h-4 text-white flex-shrink-0" />
        <div>
          <div className="text-sm font-bold text-white">Import from Estimate Import Template</div>
          <div className="text-xs text-blue-300 mt-0.5">Upload the client's Excel schedule to populate this library</div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Source instructions */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800 leading-relaxed">
          <div className="font-semibold mb-1 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" />Where to get the template</div>
          <p>{lib.templateSource}</p>
        </div>

        {/* Excel column spec */}
        <div>
          <div className="text-xs font-semibold text-gray-700 mb-2">Required Excel column format</div>
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-3 py-2 font-medium text-gray-600 w-8">Col</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Header</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Notes</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600 w-20">Required</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {EXCEL_COLUMNS.map(c => (
                  <tr key={c.col} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono font-bold text-gray-600">{c.col}</td>
                    <td className="px-3 py-2 font-mono text-[#1F3864]">{c.header}</td>
                    <td className="px-3 py-2 text-gray-500">{c.note}</td>
                    <td className="px-3 py-2 text-center">
                      {c.required
                        ? <span className="text-red-600 font-semibold">Yes</span>
                        : <span className="text-gray-400">No</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-gray-400 mt-1.5">
            If the client's own spreadsheet matches these columns, it can be uploaded directly.
            Otherwise, download the platform template and paste the client's data into it.
          </p>
        </div>

        {/* Schedule version */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Schedule version (optional)</label>
          <input
            type="text"
            value={scheduleVer}
            onChange={e => setScheduleVer(e.target.value)}
            placeholder={`e.g. TMR PCEM 9th Ed, MRS v4.2 — Jan 2026`}
            className="input text-xs"
          />
        </div>

        {/* Upload zone */}
        {step === 'idle' && (
          <div
            onClick={simulateUpload}
            className="border-2 border-dashed border-gray-300 hover:border-[#2E75B6] rounded-xl p-8 text-center cursor-pointer transition-colors group"
          >
            <Upload className="w-8 h-8 text-gray-300 group-hover:text-[#2E75B6] mx-auto mb-2 transition-colors" />
            <div className="text-sm font-medium text-gray-600 group-hover:text-gray-800">
              Click to upload Excel file (.xlsx)
            </div>
            <div className="text-xs text-gray-400 mt-1">or drag and drop</div>
            <div className="text-[10px] text-gray-400 mt-3">Platform simulates upload for prototype — click to run validation preview</div>
          </div>
        )}

        {step === 'validating' && (
          <div className="flex items-center gap-3 py-6 justify-center">
            <RefreshCw className="w-5 h-5 text-[#2E75B6] animate-spin" />
            <div className="text-sm text-gray-600">Validating column headers and detecting changes…</div>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
              <div className="text-xs font-semibold text-blue-900 mb-2">Validation preview</div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-white rounded-lg p-2.5 border border-blue-100">
                  <div className="text-lg font-bold text-green-600">{lib.items.length}</div>
                  <div className="text-[10px] text-gray-500">Items to update</div>
                </div>
                <div className="bg-white rounded-lg p-2.5 border border-blue-100">
                  <div className="text-lg font-bold text-blue-600">0</div>
                  <div className="text-[10px] text-gray-500">New codes to add</div>
                </div>
                <div className="bg-white rounded-lg p-2.5 border border-blue-100">
                  <div className="text-lg font-bold text-gray-500">0</div>
                  <div className="text-[10px] text-gray-500">Validation errors</div>
                </div>
              </div>
              <p className="text-[10px] text-blue-700 mt-2">
                ✓ All {lib.items.length} illustrative items will be confirmed as official schedule rates.
                After import, the library status changes to "Imported" and A12/A13 can use these codes.
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={confirmImport} className="flex-1 btn-primary text-sm flex items-center justify-center gap-2">
                <CheckCircle className="w-4 h-4" />Confirm import ({lib.items.length} codes)
              </button>
              <button onClick={() => setStep('idle')} className="btn-secondary text-sm">Cancel</button>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div className="text-sm text-green-800 font-medium">Library imported successfully — status updated to Imported</div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Add / Edit single code modal ──────────────────────────────────

function AddCodeModal({ clientId, onClose, onSaved }: {
  clientId: string; onClose: () => void; onSaved: () => void
}) {
  const { user } = useAuthStore()
  const [mrsGroup, setMrsGroup] = useState('MRS93')
  const [workItemId, setWorkItemId] = useState('')
  const [description, setDescription] = useState('')
  const [unit, setUnit] = useState('ea')
  const [rate, setRate] = useState('')
  const [type, setType] = useState<CostCodeType>('L')
  const [expenseClass, setExpenseClass] = useState<ExpenseClass>('CAPEX')
  const [wiRef, setWiRef] = useState('')
  const [notes, setNotes] = useState('')

  const mrsGroupObj = TMR_MRS_GROUPS.find(g => g.code === mrsGroup)

  const handleSave = () => {
    if (!workItemId.trim() || !description.trim() || !rate) return
    addCostCodeItem(clientId, {
      mrsGroup, mrsGroupDesc: mrsGroupObj?.description ?? mrsGroup,
      workItemId: workItemId.trim(),
      description: description.trim(),
      unit, rateExGST: parseFloat(rate),
      type, expenseClass,
      wiRef: wiRef.trim() || undefined,
      effectiveDate: new Date().toISOString().slice(0, 10),
      isActive: true,
      scheduleVersion: 'Manual entry',
      notes: notes.trim() || undefined,
      isIllustrative: false,
    }, user?.fullName ?? 'PM', user?.id ?? 'u-sys')
    onSaved()
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="text-sm font-semibold text-gray-900">Add single cost code</div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">MRS group <span className="text-red-500">*</span></label>
              <select value={mrsGroup} onChange={e => setMrsGroup(e.target.value)} className="input text-xs">
                {TMR_MRS_GROUPS.map(g => <option key={g.code} value={g.code}>{g.code} — {g.description.slice(0, 35)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Work Item ID <span className="text-red-500">*</span></label>
              <input type="text" value={workItemId} onChange={e => setWorkItemId(e.target.value)} placeholder="e.g. 93001L" className="input text-xs" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description <span className="text-red-500">*</span></label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="Client-published description" className="input text-xs" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Unit <span className="text-red-500">*</span></label>
              <select value={unit} onChange={e => setUnit(e.target.value)} className="input text-xs">
                {['ea','m','m²','m³','hr','LS','day','week','tonne'].map(u => <option key={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Rate (ex GST) <span className="text-red-500">*</span></label>
              <input type="number" value={rate} onChange={e => setRate(e.target.value)} placeholder="0.00" className="input text-xs" step="0.01" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <select value={type} onChange={e => setType(e.target.value as CostCodeType)} className="input text-xs">
                <option value="L">L — Labour</option>
                <option value="M">M — Materials</option>
                <option value="E">E — Equipment</option>
                <option value="C">C — Combined</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">CapEx / OpEx</label>
              <select value={expenseClass} onChange={e => setExpenseClass(e.target.value as ExpenseClass)} className="input text-xs">
                <option value="CAPEX">CapEx (per TMR Annexure N)</option>
                <option value="OPEX">OpEx</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">WI reference (optional)</label>
              <input type="text" value={wiRef} onChange={e => setWiRef(e.target.value)} placeholder="e.g. TMR MRS SD ITS-0042" className="input text-xs" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
            <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Internal PM/PC reference notes" className="input text-xs" />
          </div>
        </div>
        <div className="flex gap-2 px-6 py-4 border-t">
          <button onClick={handleSave} disabled={!workItemId.trim() || !description.trim() || !rate} className="btn-primary flex-1 text-sm disabled:opacity-40">Save cost code</button>
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
        </div>
      </div>
    </div>
  )
}

// ── Code register table ───────────────────────────────────────────

function CodeRegister({ lib }: { lib: ClientCostLibrary }) {
  const { user, isTier } = useAuthStore()
  const isAdmin = isTier('A_DEVELOPER')
  const isPM    = isTier('B_PM')
  const canEdit = isAdmin || isPM

  const [search, setSearch]       = useState('')
  const [mrsFilter, setMrsFilter] = useState('ALL')
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'L' | 'M' | 'E' | 'C'>('ALL')
  const [showInactive, setShowInactive] = useState(false)
  const [addOpen, setAddOpen]     = useState(false)
  const [refresh, setRefresh]     = useState(0)

  const allGroups = [...new Set(lib.items.map(i => i.mrsGroup))].sort()

  const filtered = useMemo(() => {
    return lib.items.filter(i => {
      if (!showInactive && !i.isActive) return false
      if (mrsFilter !== 'ALL' && i.mrsGroup !== mrsFilter) return false
      if (typeFilter !== 'ALL' && i.type !== typeFilter) return false
      if (search) {
        const hay = `${i.workItemId} ${i.description} ${i.mrsGroup} ${i.wiRef ?? ''}`.toLowerCase()
        if (!hay.includes(search.toLowerCase())) return false
      }
      return true
    })
  }, [lib.items, search, mrsFilter, typeFilter, showInactive, refresh])

  const handleDeactivate = (item: CostCodeItem) => {
    deactivateItem(lib.clientId, item.id, user?.fullName ?? 'PM', user?.id ?? 'u-sys')
    setRefresh(r => r + 1)
  }

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-48">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" placeholder="Search codes, descriptions, WI refs…"
            value={search} onChange={e => setSearch(e.target.value)}
            className="input pl-8 text-xs"
          />
        </div>
        <select value={mrsFilter} onChange={e => setMrsFilter(e.target.value)} className="input text-xs w-44">
          <option value="ALL">All MRS groups</option>
          {allGroups.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)} className="input text-xs w-36">
          <option value="ALL">All types</option>
          <option value="L">L — Labour</option>
          <option value="M">M — Materials</option>
          <option value="E">E — Equipment</option>
          <option value="C">C — Combined</option>
        </select>
        <button
          onClick={() => setShowInactive(!showInactive)}
          className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border transition-colors ${showInactive ? 'bg-gray-100 border-gray-300 text-gray-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}
        >
          {showInactive ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          {showInactive ? 'Hiding inactive' : 'Show inactive'}
        </button>
        {canEdit && (
          <button onClick={() => setAddOpen(true)} className="btn-primary text-xs flex items-center gap-1.5 flex-shrink-0">
            <Plus className="w-3.5 h-3.5" />Add code
          </button>
        )}
      </div>

      {/* Count */}
      <div className="text-xs text-gray-500">
        Showing {filtered.length} of {lib.items.length} codes
        {lib.importStatus === 'ILLUSTRATIVE' && (
          <span className="ml-2 text-amber-600 font-medium">⚠ Illustrative rates — not from official template</span>
        )}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl py-16 text-center">
          <Tag className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <div className="text-sm font-medium text-gray-500">No cost codes match your filters</div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-3 py-2.5 font-medium text-gray-600">MRS Group</th>
                <th className="text-left px-3 py-2.5 font-medium text-gray-600">Work Item ID</th>
                <th className="text-left px-4 py-2.5 font-medium text-gray-600">Description</th>
                <th className="text-left px-3 py-2.5 font-medium text-gray-600 w-10">Unit</th>
                <th className="text-right px-3 py-2.5 font-medium text-gray-600">Rate (ex GST)</th>
                <th className="text-center px-2 py-2.5 font-medium text-gray-600">Type</th>
                <th className="text-center px-2 py-2.5 font-medium text-gray-600">CapEx</th>
                <th className="text-left px-3 py-2.5 font-medium text-gray-600">WI Ref</th>
                <th className="text-center px-2 py-2.5 font-medium text-gray-600 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((item, i) => {
                const typeMeta    = COST_CODE_TYPE_META[item.type]
                const expMeta     = EXPENSE_CLASS_META[item.expenseClass]
                return (
                  <tr key={item.id} className={`hover:bg-gray-50 ${!item.isActive ? 'opacity-50' : ''} ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                    <td className="px-3 py-2">
                      <span className="font-mono font-semibold text-[#1F3864] text-[10px]">{item.mrsGroup}</span>
                      {item.isIllustrative && <span className="ml-1 text-[9px] text-amber-600 font-medium">~</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-gray-700 text-[10px]">{item.workItemId}</td>
                    <td className="px-4 py-2 text-gray-800 max-w-xs truncate" title={item.description}>{item.description}</td>
                    <td className="px-3 py-2 text-gray-500 text-center">{item.unit}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-gray-900">{fmtAUD(item.rateExGST)}</td>
                    <td className="px-2 py-2 text-center">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${typeMeta.bg} ${typeMeta.text}`}>
                        {item.type}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${expMeta.bg} ${expMeta.text}`}>
                        {item.expenseClass === 'CAPEX' ? 'Cap' : 'Op'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-400 text-[10px] max-w-xs truncate" title={item.wiRef}>{item.wiRef ?? '—'}</td>
                    <td className="px-2 py-2 text-center">
                      {canEdit && item.isActive && (
                        <button
                          onClick={() => handleDeactivate(item)}
                          title="Deactivate (retains history)"
                          className="text-gray-300 hover:text-red-500 transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {addOpen && (
        <AddCodeModal
          clientId={lib.clientId}
          onClose={() => setAddOpen(false)}
          onSaved={() => setRefresh(r => r + 1)}
        />
      )}
    </div>
  )
}

// ── Audit log panel ───────────────────────────────────────────────

function AuditLog({ lib }: { lib: ClientCostLibrary }) {
  const ACTION_META: Record<string, { label: string; bg: string; text: string }> = {
    IMPORT_BULK:  { label: 'Bulk import',    bg: 'bg-blue-100',   text: 'text-blue-700'   },
    ADD_SINGLE:   { label: 'Added',          bg: 'bg-green-100',  text: 'text-green-700'  },
    EDIT:         { label: 'Edited',         bg: 'bg-amber-100',  text: 'text-amber-700'  },
    DEACTIVATE:   { label: 'Deactivated',    bg: 'bg-red-100',    text: 'text-red-700'    },
    ACTIVATE:     { label: 'Reactivated',    bg: 'bg-green-100',  text: 'text-green-700'  },
    RATE_UPDATE:  { label: 'Rate updated',   bg: 'bg-purple-100', text: 'text-purple-700' },
  }
  if (lib.auditLog.length === 0) {
    return (
      <div className="bg-white border border-dashed border-gray-200 rounded-xl py-12 text-center">
        <History className="w-8 h-8 text-gray-200 mx-auto mb-2" />
        <div className="text-sm text-gray-400">No changes recorded yet</div>
      </div>
    )
  }
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="divide-y divide-gray-50">
        {lib.auditLog.map(entry => {
          const meta = ACTION_META[entry.action] ?? { label: entry.action, bg: 'bg-gray-100', text: 'text-gray-600' }
          return (
            <div key={entry.id} className="px-4 py-3 flex items-start gap-3">
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0 mt-0.5 ${meta.bg} ${meta.text}`}>
                {meta.label}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-800">{entry.description}</p>
                {entry.batchRef && <span className="text-[10px] font-mono text-gray-400">{entry.batchRef}</span>}
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-[10px] text-gray-500">{entry.userName}</div>
                <div className="text-[10px] text-gray-400">{fmtDT(entry.timestamp)}</div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────

export function CostCodes() {
  const { isTier } = useAuthStore()
  const isAdmin = isTier('A_DEVELOPER')
  const isPM    = isTier('B_PM')
  const canEdit = isAdmin || isPM

  const [libraries, setLibraries]   = useState(() => getAllLibraries())
  const [activeClient, setActiveClient] = useState(libraries[0]?.clientId ?? 'tmr')
  const [tab, setTab]               = useState<'register' | 'import' | 'audit'>('register')
  const [addClientOpen, setAddClientOpen] = useState(false)
  const [newClientName, setNewClientName] = useState('')
  const [newClientType, setNewClientType] = useState<CostCodeClientType>('BCC')
  const [newClientSource, setNewClientSource] = useState('')

  const refresh = () => setLibraries(getAllLibraries())

  const lib = libraries.find(l => l.clientId === activeClient) ?? libraries[0]
  if (!lib) return <div className="p-8 text-gray-400">No libraries found.</div>

  const statusMeta = IMPORT_STATUS_META[lib.importStatus]
  const clientMeta = CLIENT_TYPE_META[lib.clientType]

  const handleAddClient = () => {
    if (!newClientName.trim()) return
    const id = newClientName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    addClientLibrary(id, newClientName.trim(), newClientType, newClientSource.trim() || 'Request schedule of rates from client project manager.')
    setAddClientOpen(false)
    setNewClientName(''); setNewClientSource('')
    const updated = getAllLibraries()
    setLibraries(updated)
    setActiveClient(id)
  }

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Tag className="w-5 h-5 text-[#2E75B6]" />
            Client Cost Code Libraries
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Per-client MRS-based cost code management · BOQ pre-fill · progress claim validation · Xero allocation
          </p>
        </div>
        {canEdit && (
          <button onClick={() => setAddClientOpen(true)} className="btn-secondary text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" />Add client
          </button>
        )}
      </div>

      {/* Agent advisory banner */}
      {libraries.some(l => l.importStatus === 'PENDING_IMPORT' || l.importStatus === 'ILLUSTRATIVE') && (
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <Bot className="w-4 h-4 text-[#2E75B6] flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-800">
            <strong>A12 QS Agent · A13 CA Agent</strong> — One or more client libraries are pending import or contain illustrative data.
            AI cost code pre-fill on BOQ lines and claim line validation are limited until official schedule templates are imported.
            A13 will flag uncoded claim lines as HIGH RISK on TMR and Council contracts.
          </div>
        </div>
      )}

      {/* Client selector */}
      <div className="flex gap-2 items-center flex-wrap">
        <span className="text-xs text-gray-500 flex-shrink-0">Client:</span>
        {libraries.map(l => {
          const meta   = CLIENT_TYPE_META[l.clientType]
          const stMeta = IMPORT_STATUS_META[l.importStatus]
          return (
            <button
              key={l.clientId}
              onClick={() => { setActiveClient(l.clientId); setTab('register') }}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                activeClient === l.clientId
                  ? 'bg-[#1F3864] text-white border-[#1F3864]'
                  : `bg-white border-gray-200 text-gray-600 hover:bg-gray-50`
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${
                l.importStatus === 'IMPORTED' ? 'bg-green-400' :
                l.importStatus === 'ILLUSTRATIVE' ? 'bg-amber-400' : 'bg-gray-300'
              }`} />
              {meta.abbr}
              <span className={`text-[10px] font-semibold ${activeClient === l.clientId ? 'text-blue-200' : 'text-gray-400'}`}>
                {l.items.length > 0 ? `${l.items.length}` : '0'}
              </span>
            </button>
          )
        })}
      </div>

      {/* Library header */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 bg-[#1F3864] flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-sm font-bold text-white">{lib.clientName}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${clientMeta.bg} ${clientMeta.text}`}>
                {clientMeta.abbr}
              </span>
            </div>
            {lib.scheduleVersion && (
              <div className="text-xs text-blue-300">{lib.scheduleVersion}</div>
            )}
          </div>
          <div className="text-right">
            <div className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-semibold border ${statusMeta.bg} ${statusMeta.text} ${statusMeta.border}`}>
              {lib.importStatus === 'IMPORTED' ? <CheckCircle className="w-3 h-3" /> :
               lib.importStatus === 'ILLUSTRATIVE' ? <AlertTriangle className="w-3 h-3" /> :
               <AlertCircle className="w-3 h-3" />}
              {statusMeta.label}
            </div>
            {lib.importedAt && (
              <div className="text-[10px] text-blue-300 mt-1">
                Imported {fmtDT(lib.importedAt)} · {lib.importedByName}
              </div>
            )}
          </div>
        </div>

        {/* Status description */}
        <div className={`px-5 py-3 border-b text-xs ${statusMeta.bg} ${statusMeta.text} border-opacity-50`}>
          <div className="flex items-start gap-2">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <p>{statusMeta.description}</p>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-4 divide-x text-xs">
          {[
            { label: 'Total codes', value: lib.items.length, color: 'text-[#1F3864]' },
            { label: 'Active', value: lib.items.filter(i => i.isActive).length, color: 'text-green-700' },
            { label: 'MRS groups', value: new Set(lib.items.map(i => i.mrsGroup)).size, color: 'text-blue-700' },
            { label: 'Illustrative', value: lib.items.filter(i => i.isIllustrative).length, color: lib.items.some(i => i.isIllustrative) ? 'text-amber-600' : 'text-gray-400' },
          ].map(({ label, value, color }) => (
            <div key={label} className="px-4 py-3 text-center">
              <div className={`text-xl font-bold ${color}`}>{value}</div>
              <div className="text-gray-500 mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tab strip */}
      <div className="flex border-b border-gray-200">
        {([
          { key: 'register', label: 'Code register', icon: Tag },
          { key: 'import',   label: 'Import template', icon: Upload, badge: lib.importStatus !== 'IMPORTED' },
          { key: 'audit',    label: 'Audit log',    icon: History },
        ] as const).map(({ key, label, icon: Icon, badge }: any) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === key ? 'border-[#2E75B6] text-[#2E75B6]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}>
            <Icon className="w-4 h-4" />{label}
            {badge && <span className="ml-1 bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 rounded-full font-semibold">Action</span>}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'register' && <CodeRegister lib={lib} key={lib.clientId} />}
      {tab === 'import'   && <ImportPanel lib={lib} onImported={refresh} />}
      {tab === 'audit'    && <AuditLog lib={lib} />}

      {/* Add client modal */}
      {addClientOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="text-sm font-semibold text-gray-900">Add new client library</div>
              <button onClick={() => setAddClientOpen(false)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Client name <span className="text-red-500">*</span></label>
                <input type="text" value={newClientName} onChange={e => setNewClientName(e.target.value)} placeholder="e.g. Brisbane City Council" className="input text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Client type</label>
                <select value={newClientType} onChange={e => setNewClientType(e.target.value as CostCodeClientType)} className="input text-sm">
                  <option value="BCC">BCC — Brisbane City Council</option>
                  <option value="SCC">SCC — Sunshine Coast Council</option>
                  <option value="TMR">TMR — Transport & Main Roads</option>
                  <option value="ENERGEX">Energex</option>
                  <option value="ERGON">Ergon Energy</option>
                  <option value="INTERNAL">Internal</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Template source instructions</label>
                <textarea
                  value={newClientSource} onChange={e => setNewClientSource(e.target.value)}
                  placeholder="Where to obtain the client's Estimate Import Template or Schedule of Rates…"
                  className="input text-xs h-20 resize-none"
                />
              </div>
            </div>
            <div className="flex gap-2 px-6 py-4 border-t">
              <button onClick={handleAddClient} disabled={!newClientName.trim()} className="btn-primary flex-1 text-sm disabled:opacity-40">Add library</button>
              <button onClick={() => setAddClientOpen(false)} className="btn-secondary text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
